import { useState, useEffect } from "react";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { ChromePicker } from "react-color";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { anatomyConstants } from "../../../constants/anatomyConstants";

const ModelsSidebar = ({
  modelsRefs,
  onOpacityChange,
  onVisibilityChange,
  onColorChange,
  onColorSave,
  onMoveToStructure,
  modelGroups = [],
  isTargetMeshMode = false,
  toggleTargetMeshMode = () => {},
  targetedIndices = [],
  onGroupColorSave = () => {}, // NEW: optional callback for saving group color
  onOpenGroupingModal = () => {}, // NEW: callback to open grouping modal
}) => {
  const [visibility, setVisibility] = useState([]);
  const [opacity, setOpacity] = useState([]);
  const [colors, setColors] = useState([]);
  const [showColorPicker, setShowColorPicker] = useState({});
  const [filterText, setFilterText] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedModels, setSelectedModels] = useState(new Set()); // Track selected model indices

  useEffect(() => {
    setVisibility(modelsRefs.map((model) => model.visible));
    setOpacity(modelsRefs.map((model) => model.material.opacity));

    // Create a map of index -> group info for meshes in groups
    const indexToGroupInfo = new Map();
    modelGroups.forEach((group, groupIdx) => {
      group.indices.forEach((idx) => {
        indexToGroupInfo.set(idx, { color: group.color, groupIdx });
      });
    });

    // Set colors: use group color if mesh is in a collapsed group, otherwise use mesh's own color
    setColors(
      modelsRefs.map((model, index) => {
        const groupInfo = indexToGroupInfo.get(index);
        if (groupInfo && !expandedGroups.has(groupInfo.groupIdx)) {
          // Use group color if in a collapsed group
          return groupInfo.color;
        }
        // Use original individual color if not in group or group is expanded
        return model.userData?.originalColor || model.material.color.getStyle();
      })
    );

    setShowColorPicker({});
  }, [modelsRefs, modelGroups, expandedGroups]);

  const handleVisibilityChange = (
    index,
    isGroup = false,
    groupIndices = []
  ) => {
    const indicesToChange = isGroup ? groupIndices : [index];

    const newVisibility = [...visibility];
    const newOpacity = [...opacity];

    // Determine target state from first model
    const firstModel = modelsRefs[indicesToChange[0]];
    const targetVisible = !firstModel.visible;

    indicesToChange.forEach((idx) => {
      const model = modelsRefs[idx];
      if (model) {
        newVisibility[idx] = targetVisible;

        if (targetVisible) {
          model.material.opacity = 1;
          newOpacity[idx] = 1;
        } else {
          model.material.opacity = 0;
          newOpacity[idx] = 0;
        }

        model.material.needsUpdate = true;
        model.visible = newVisibility[idx];

        onVisibilityChange(idx, newVisibility[idx]);
        onOpacityChange(idx, newOpacity[idx]);
      }
    });

    setVisibility(newVisibility);
    setOpacity(newOpacity);
  };

  const handleOpacityChange = (
    index,
    value,
    isGroup = false,
    groupIndices = []
  ) => {
    const indicesToChange = isGroup ? groupIndices : [index];
    const opacityChange = [...opacity];

    indicesToChange.forEach((idx) => {
      const model = modelsRefs[idx];
      if (model) {
        model.material.opacity = value;
        model.material.needsUpdate = true;
        opacityChange[idx] = value;

        if (value === 0 && model.visible) {
          model.visible = false;
          onVisibilityChange(idx, false);
        } else if (value > 0 && !model.visible) {
          model.visible = true;
          onVisibilityChange(idx, true);
        }

        onOpacityChange(idx, value);
      }
    });

    setOpacity(opacityChange);
  };

  const handleColorChange = (
    color,
    index,
    isGroup = false,
    groupIndices = []
  ) => {
    const indicesToChange = isGroup ? groupIndices : [index];
    const newColors = [...colors];

    indicesToChange.forEach((idx) => {
      newColors[idx] = color.hex;
      onColorChange(idx, color.hex);
    });

    setColors(newColors);
  };

  const toggleColorPicker = (key) => {
    setShowColorPicker((prev) => {
      const newPickers = {};
      // Close all other pickers, toggle the clicked one
      Object.keys(prev).forEach((k) => {
        newPickers[k] = false;
      });
      newPickers[key] = !prev[key];
      return newPickers;
    });
  };

  const handleCloseColorPicker = (key) => {
    setShowColorPicker((prev) => ({
      ...prev,
      [key]: false,
    }));
  };

  // Generic save for individual models / group members (per-mesh color)
  const handleSaveColor = (key, index, isGroup = false, groupIndices = []) => {
    const indicesToChange = isGroup ? groupIndices : [index];

    indicesToChange.forEach((idx) => {
      const selectedColor = colors[idx];
      onColorSave(idx, selectedColor);
    });

    handleCloseColorPicker(key);
  };

  // Dedicated group color save (group-level color, not per-mesh)
  const handleSaveGroupColor = (groupIdx, groupIndices = []) => {
    if (!groupIndices.length) {
      handleCloseColorPicker(`group-${groupIdx}`);
      return;
    }
    const firstIndex = groupIndices[0];
    const selectedColor = colors[firstIndex];

    onGroupColorSave(groupIdx, selectedColor);
    handleCloseColorPicker(`group-${groupIdx}`);
  };

  const makeOthersInvisible = (index, isGroup = false, groupIndices = []) => {
    const visibleIndices = isGroup ? groupIndices : [index];
    const newVisibility = modelsRefs.map((_, i) => visibleIndices.includes(i));
    const newOpacity = modelsRefs.map((_, i) =>
      visibleIndices.includes(i) ? 1 : 0
    );

    modelsRefs.forEach((model, i) => {
      model.visible = newVisibility[i];
      model.material.opacity = newOpacity[i];
      model.material.needsUpdate = true;
      onVisibilityChange(i, newVisibility[i]);
      onOpacityChange(i, newOpacity[i]);
    });
    setVisibility(newVisibility);
    setOpacity(newOpacity);
  };

  const showAllStructures = () => {
    const newVisibility = modelsRefs.map(() => true);
    const newOpacity = modelsRefs.map(() => 1);
    modelsRefs.forEach((model, i) => {
      model.visible = true;
      model.material.opacity = 1;
      model.material.needsUpdate = true;
      onVisibilityChange(i, true);
      onOpacityChange(i, 1);
    });
    setVisibility(newVisibility);
    setOpacity(newOpacity);
  };

  const moveToStructure = (index) => {
    if (onMoveToStructure) {
      onMoveToStructure(index);
    }
  };

  // --- New toggle function for sorting ---
  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  // --- Selection handlers ---
  const toggleModelSelection = (index) => {
    setSelectedModels((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleGroupSelection = (groupIndices) => {
    setSelectedModels((prev) => {
      const newSet = new Set(prev);
      // Check if all indices are selected
      const allSelected = groupIndices.every((idx) => newSet.has(idx));
      
      if (allSelected) {
        // Deselect all
        groupIndices.forEach((idx) => newSet.delete(idx));
      } else {
        // Select all
        groupIndices.forEach((idx) => newSet.add(idx));
      }
      return newSet;
    });
  };

  const showSelectedModels = () => {
    if (selectedModels.size > 0) {
      // Show only selected models
      const newVisibility = modelsRefs.map((_, i) => selectedModels.has(i));
      const newOpacity = modelsRefs.map((_, i) => (selectedModels.has(i) ? 1 : 0));

      modelsRefs.forEach((model, i) => {
        model.visible = newVisibility[i];
        model.material.opacity = newOpacity[i];
        model.material.needsUpdate = true;
        onVisibilityChange(i, newVisibility[i]);
        onOpacityChange(i, newOpacity[i]);
      });
      setVisibility(newVisibility);
      setOpacity(newOpacity);
      
      // Clear selections after showing
      setSelectedModels(new Set());
    }
  };

  // --- Toggle group expansion ---
  const toggleGroupExpansion = (groupIdx) => {
    const isCurrentlyExpanded = expandedGroups.has(groupIdx);
    const group = modelGroups[groupIdx];

    if (group && group.indices) {
      const newColors = [...colors];
      group.indices.forEach((idx) => {
        const model = modelsRefs[idx];
        if (model) {
          if (isCurrentlyExpanded) {
            // Collapsing: use group color
            newColors[idx] = group.color;
            onColorChange(idx, group.color);
          } else {
            // Expanding: use original individual mesh color from userData
            const originalColor =
              model.userData?.originalColor || model.material.color.getStyle();
            newColors[idx] = originalColor;
            onColorChange(idx, originalColor);
          }
        }
      });
      setColors(newColors);
    }

    // Toggle expansion state
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupIdx)) {
        newSet.delete(groupIdx);
      } else {
        newSet.add(groupIdx);
      }
      return newSet;
    });
  };

  // --- Updated logic for filtering and sorting with groups ---
  // Get all indices that are part of groups
  const groupedIndices = new Set();
  modelGroups.forEach((group) => {
    (group.indices || []).forEach((idx) => groupedIndices.add(idx));
  });

  // Helper function to check if a model index is in a group
  const isModelInGroup = (index) => groupedIndices.has(index);

  // Precompute all group members for search & expansion
  const groupMembersByGroup = new Map();
  modelGroups.forEach((group, groupIdx) => {
    const indices = group.indices || [];
    const members = indices
      .map((idx) => {
        const model = modelsRefs[idx];
        if (!model) return null;
        return {
          isGroup: false,
          isGroupMember: true,
          parentGroupIndex: groupIdx,
          model,
          originalIndex: idx,
          name: model.name,
          visible: model.visible,
          opacity: model.material.opacity,
          targetKey: `mesh-${idx}`,
        };
      })
      .filter(Boolean);
    groupMembersByGroup.set(groupIdx, members);
  });

  // Filter helpers
  const normalizedFilter = filterText.trim().toLowerCase();
  const matchesFilter = (name) =>
    !normalizedFilter || name.toLowerCase().includes(normalizedFilter);

  const itemMatchesFilter = (item) => {
    // No filter → everything passes
    if (!normalizedFilter) return true;

    // Direct name match
    if (matchesFilter(item.name)) return true;

    // For a group, also match if ANY of its children match
    if (item.isGroup) {
      const members = groupMembersByGroup.get(item.groupIndex) || [];
      return members.some((m) => matchesFilter(m.name));
    }

    return false;
  };

  // Create display items for groups (no members yet)
  const groupItems = modelGroups
    .map((group, groupIdx) => {
      const indices = group.indices || [];
      if (!indices.length) return null;

      const firstIdx = indices[0];
      const firstModel = modelsRefs[firstIdx];

      return {
        isGroup: true,
        isGroupMember: false,
        groupIndex: groupIdx,
        name: group.name,
        indices,
        color: group.color,
        visible: firstModel?.visible ?? true,
        opacity: firstModel?.material?.opacity ?? 1,
        targetKey: `group-${groupIdx}`,
      };
    })
    .filter(Boolean);

  // Create display items for individual models (not in any group)
  const individualItems = modelsRefs
    .map((model, index) => ({
      isGroup: false,
      isGroupMember: false,
      model,
      originalIndex: index,
      name: model.name,
      visible: model.visible,
      opacity: model.material.opacity,
      targetKey: `mesh-${index}`,
    }))
    .filter((item) => !groupedIndices.has(item.originalIndex));

  // Split groups / individuals into targeted vs non-targeted
  const targetedGroups = [];
  const nonTargetedGroups = [];
  groupItems.forEach((g) => {
    if (targetedIndices.includes(g.targetKey)) targetedGroups.push(g);
    else nonTargetedGroups.push(g);
  });

  const targetedIndividuals = [];
  const nonTargetedIndividuals = [];
  individualItems.forEach((item) => {
    if (targetedIndices.includes(item.targetKey))
      targetedIndividuals.push(item);
    else nonTargetedIndividuals.push(item);
  });

  // Sort helper
  const compareByName = (a, b) =>
    sortOrder === "asc"
      ? a.name.localeCompare(b.name)
      : b.name.localeCompare(a.name);

  // --- Build NON-TARGETED list: groups (+children if expanded) then standalone meshes ---

  const filteredNonTargetedGroups = nonTargetedGroups.filter(itemMatchesFilter);
  const filteredNonTargetedIndividuals =
    nonTargetedIndividuals.filter(itemMatchesFilter);

  filteredNonTargetedGroups.sort(compareByName);
  filteredNonTargetedIndividuals.sort(compareByName);

  const finalNonTargetedItems = [];
  filteredNonTargetedGroups.forEach((groupItem) => {
    finalNonTargetedItems.push(groupItem);

    // Only show children when group is expanded
    if (expandedGroups.has(groupItem.groupIndex)) {
      const members = groupMembersByGroup.get(groupItem.groupIndex) || [];
      const visibleMembers = !normalizedFilter
        ? members
        : members.filter((m) => matchesFilter(m.name));

      visibleMembers.sort(compareByName);
      finalNonTargetedItems.push(...visibleMembers);
    }
  });

  // Finally, add standalone meshes that are not in any group
  finalNonTargetedItems.push(...filteredNonTargetedIndividuals);

  // --- Build TARGETED list: respect pin order + support "Show Models" there too ---

  // First order targeted groups / individuals according to targetedIndices
  const targetedAll = [...targetedGroups, ...targetedIndividuals];
  targetedAll.sort(
    (a, b) =>
      targetedIndices.indexOf(a.targetKey) -
      targetedIndices.indexOf(b.targetKey)
  );

  const filteredTargetedItems = targetedAll.filter(itemMatchesFilter);

  const finalTargetedItems = [];
  filteredTargetedItems.forEach((item) => {
    finalTargetedItems.push(item);

    // If this is a group and it's expanded → show its members here too
    if (item.isGroup && expandedGroups.has(item.groupIndex)) {
      const members = groupMembersByGroup.get(item.groupIndex) || [];
      const visibleMembers = !normalizedFilter
        ? members
        : members.filter((m) => matchesFilter(m.name));

      visibleMembers.sort(compareByName);
      finalTargetedItems.push(...visibleMembers);
    }
  });

  // Final list: targeted at top, then normal
  const displayModels = [...finalTargetedItems, ...finalNonTargetedItems];

  return (
    <div
      className="absolute right-0 top-12 w-80 bg-slate-50/95 backdrop-blur-xl border-l border-slate-200/60 shadow-2xl z-20 h-[calc(100%-3rem)] flex flex-col transition-all duration-300"
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div className="bg-gradient-to-r from-[var(--main-color)] to-[var(--main-color-bold)] text-white p-3 flex items-center justify-between shadow-md flex-shrink-0 z-10">
        <div className="flex space-x-2">
          <button
            className="px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 active:bg-white/30 transition-all duration-200 text-xs font-bold tracking-wide backdrop-blur-sm border border-white/10"
            onClick={showAllStructures}
          >
            Show All
          </button>
          <button
            className={`px-3 py-1.5 rounded-md transition-all duration-200 text-xs font-bold tracking-wide backdrop-blur-sm border border-white/10 ${
              selectedModels.size > 0
                ? "bg-white/10 hover:bg-white/20 active:bg-white/30"
                : "bg-black/5 opacity-50 cursor-not-allowed"
            }`}
            onClick={showSelectedModels}
            disabled={selectedModels.size === 0}
            title={
              selectedModels.size > 0
                ? `Show ${selectedModels.size} selected model${selectedModels.size > 1 ? 's' : ''}`
                : "Select models first"
            }
          >
            Show Selected
          </button>
        </div>
        <button
          className="p-1.5 rounded-md bg-white/10 hover:bg-white/20 transition-all duration-200 border border-white/10 backdrop-blur-sm flex items-center justify-center"
          onClick={onOpenGroupingModal}
          title="Group Models"
        >
          <AccountTreeOutlinedIcon fontSize="small" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Filter/Sort/Target bar */}
        <div className="p-3 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center space-x-2 sticky top-0 z-10 shadow-sm">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Filter by name..."
              className="w-full pl-3 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]/30 focus:border-[var(--main-color)] transition-all duration-200 shadow-inner"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            {filterText && (
              <button 
                onClick={() => setFilterText("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            )}
          </div>
          <button
            onClick={toggleSortOrder}
            className="p-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 transition-colors flex-shrink-0 flex items-center justify-center"
            title={`Sort ${sortOrder === "asc" ? "Descending" : "Ascending"}`}
          >
            {sortOrder === "asc" ? (
              <ArrowUpwardIcon className="text-gray-600" fontSize="small" />
            ) : (
              <ArrowDownwardIcon className="text-gray-600" fontSize="small" />
            )}
          </button>
          <button
            onClick={toggleTargetMeshMode}
            className={`p-2 rounded-lg border transition-all duration-200 flex-shrink-0 flex items-center justify-center ${
              isTargetMeshMode
                ? "bg-[var(--main-color)] border-[var(--main-color)] text-white shadow-md shadow-[var(--main-color)]/20"
                : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
            title={
              isTargetMeshMode
                ? "Target Mode Active - Click meshes to pin to top"
                : "Enable Target Mode"
            }
          >
            <MyLocationIcon fontSize="small" />
          </button>
        </div>

        <ul className="list-none m-0 p-0 pb-4">
          {displayModels.map((item) => {
            if (item.isGroup) {
              // Render group item
              const groupIndex = item.groupIndex;
              const groupIndices = item.indices;
              const firstIndex = groupIndices[0];
              const isVisible = item.visible;
              const groupOpacity = item.opacity;
              const isExpanded = expandedGroups.has(groupIndex);

              return (
                <li
                  key={`group-${groupIndex}`}
                  className="cursor-pointer mx-3 my-3 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <div className="p-3.5 flex flex-col space-y-3">
                    <div className="flex justify-between items-center w-full">
                      <div className="flex items-center space-x-3">
                        {/* HIDE color button when expanded */}
                        {!isExpanded && (
                          <div className="relative">
                            <button
                              style={{ backgroundColor: colors[firstIndex], boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)" }}
                              className="w-5 h-5 rounded-full border border-slate-200 hover:scale-110 transition-transform"
                              onClick={() => toggleColorPicker(`group-${groupIndex}`)}
                            ></button>

                            {showColorPicker[`group-${groupIndex}`] && (
                              <div className="absolute z-10 left-0 top-8 rounded-xl bg-white shadow-2xl border border-slate-200 w-56 flex flex-col items-center p-3">
                                <ChromePicker
                                  color={colors[firstIndex]}
                                  onChange={(color) => handleColorChange(color, firstIndex, true, groupIndices)}
                                  disableAlpha={true}
                                />
                                <div className="flex space-x-2 mt-3 w-full">
                                  <button
                                    className="flex-1 py-1.5 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm font-medium transition-colors"
                                    onClick={() => handleCloseColorPicker(`group-${groupIndex}`)}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    className="flex-1 py-1.5 text-white rounded-lg bg-[var(--main-color)] hover:bg-[var(--main-color-bold)] text-sm font-medium shadow-md shadow-[var(--main-color)]/20 transition-all"
                                    onClick={() => handleSaveGroupColor(groupIndex, groupIndices)}
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="text-slate-800 font-semibold text-sm tracking-tight">
                          {item.name}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={groupIndices.every((idx) => selectedModels.has(idx))}
                          onChange={() => toggleGroupSelection(groupIndices)}
                          className="w-4 h-4 cursor-pointer accent-[var(--main-color)] rounded border-slate-300 focus:ring-[var(--main-color)] transition-all"
                          title="Select all models in group for batch visibility"
                        />
                        {isVisible ? (
                          <VisibilityOutlinedIcon
                            className="text-[var(--main-color)] cursor-pointer hover:scale-110 transition-transform"
                            onClick={() => handleVisibilityChange(firstIndex, true, groupIndices)}
                            fontSize="small"
                          />
                        ) : (
                          <VisibilityOffOutlinedIcon
                            className="text-slate-300 cursor-pointer hover:scale-110 transition-transform hover:text-slate-400"
                            onClick={() => handleVisibilityChange(firstIndex, true, groupIndices)}
                            fontSize="small"
                          />
                        )}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5 flex items-center space-x-3 border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider w-14">Opacity</span>
                      <Slider
                        min={0}
                        max={1}
                        step={0.1}
                        value={groupOpacity}
                        onChange={(value) => handleOpacityChange(firstIndex, value, true, groupIndices)}
                        className="flex-grow"
                        styles={{
                          track: { backgroundColor: anatomyConstants.mainColor, height: 6 },
                          handle: {
                            borderColor: anatomyConstants.mainColor,
                            backgroundColor: "#fff",
                            width: 14,
                            height: 14,
                            marginTop: -4,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                          },
                          rail: { backgroundColor: '#e2e8f0', height: 6 }
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button
                        className="py-1.5 px-2 bg-white text-slate-700 border border-slate-200 rounded-lg text-xs font-medium hover:border-[var(--main-color)] hover:text-[var(--main-color)] hover:bg-slate-50 transition-all duration-200 flex items-center justify-center shadow-sm"
                        onClick={() => makeOthersInvisible(firstIndex, true, groupIndices)}
                      >
                        Hide Others
                      </button>
                      <button
                        className={`py-1.5 px-2 bg-white border rounded-lg text-xs font-medium transition-all duration-200 flex items-center justify-center shadow-sm ${
                          isExpanded 
                            ? "text-[var(--main-color)] border-[var(--main-color)] bg-[var(--main-color)]/5" 
                            : "text-slate-700 border-slate-200 hover:border-[var(--main-color)] hover:text-[var(--main-color)] hover:bg-slate-50"
                        }`}
                        onClick={() => toggleGroupExpansion(groupIndex)}
                      >
                        {isExpanded ? "Hide Models" : "Show Models"}
                      </button>
                    </div>
                  </div>
                </li>
              );
            } else if (item.isGroupMember) {
              // Render individual model that's a member of an expanded group
              const index = item.originalIndex;
              const modelRef = item.model;

              return (
                <li
                  key={`group-member-${index}`}
                  className="cursor-pointer mx-3 mb-2 mt-1 bg-slate-50/80 rounded-lg border border-slate-100 hover:bg-white hover:shadow-sm transition-all duration-200 relative left-4 w-[calc(100%-2rem)] overflow-hidden"
                >
                  <div className="p-3 flex flex-col space-y-3">
                    <div className="flex justify-between w-full items-center">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <button
                            style={{ backgroundColor: colors[index], boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)" }}
                            className="w-4 h-4 rounded-full border border-slate-200 hover:scale-110 transition-transform"
                            onClick={() => toggleColorPicker(`member-${index}`)}
                          ></button>

                          {showColorPicker[`member-${index}`] && (
                            <div className="absolute z-10 left-0 top-6 rounded-xl bg-white shadow-2xl border border-slate-200 w-56 flex flex-col items-center p-3">
                              <ChromePicker
                                color={colors[index]}
                                onChange={(color) => handleColorChange(color, index)}
                                disableAlpha={true}
                              />
                              <div className="flex space-x-2 mt-3 w-full">
                                <button
                                  className="flex-1 py-1.5 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm font-medium transition-colors"
                                  onClick={() => handleCloseColorPicker(`member-${index}`)}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="flex-1 py-1.5 text-white rounded-lg bg-[var(--main-color)] hover:bg-[var(--main-color-bold)] text-sm font-medium shadow-md shadow-[var(--main-color)]/20 transition-all"
                                  onClick={() => handleSaveColor(`member-${index}`, index)}
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-slate-400">└</span>
                          <div className={`text-slate-700 font-medium ${modelRef.name.length > 20 ? "text-xs" : "text-sm"}`}>
                            {modelRef.name}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedModels.has(index)}
                          onChange={() => toggleModelSelection(index)}
                          className="w-3.5 h-3.5 cursor-pointer accent-[var(--main-color)] rounded border-slate-300 focus:ring-[var(--main-color)] transition-all"
                          title="Select for batch visibility"
                        />
                        {visibility[index] ? (
                          <VisibilityOutlinedIcon
                            className="text-[var(--main-color)] cursor-pointer hover:scale-110 transition-transform"
                            onClick={() => handleVisibilityChange(index)}
                            fontSize="small"
                          />
                        ) : (
                          <VisibilityOffOutlinedIcon
                            className="text-slate-300 cursor-pointer hover:scale-110 transition-transform hover:text-slate-400"
                            onClick={() => handleVisibilityChange(index)}
                            fontSize="small"
                          />
                        )}
                      </div>
                    </div>
                    <div className="bg-white rounded-md p-2 flex items-center space-x-3 border border-slate-100 shadow-inner">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider w-12">Opac</span>
                      <Slider
                        min={0}
                        max={1}
                        step={0.1}
                        value={opacity[index]}
                        onChange={(value) => handleOpacityChange(index, value)}
                        className="flex-grow"
                        styles={{
                          track: { backgroundColor: anatomyConstants.mainColor, height: 4 },
                          handle: {
                            borderColor: anatomyConstants.mainColor,
                            backgroundColor: "#fff",
                            width: 12,
                            height: 12,
                            marginTop: -4,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                          },
                          rail: { backgroundColor: '#e2e8f0', height: 4 }
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button
                        className="py-1 px-2 bg-white text-slate-600 border border-slate-200 rounded-md text-[10px] font-medium hover:border-[var(--main-color)] hover:text-[var(--main-color)] hover:bg-slate-50 transition-all duration-200 flex items-center justify-center"
                        onClick={() => makeOthersInvisible(index)}
                      >
                        Hide Others
                      </button>
                      <button
                        className="py-1 px-2 bg-white text-slate-600 border border-slate-200 rounded-md text-[10px] font-medium hover:border-[var(--main-color)] hover:text-[var(--main-color)] hover:bg-slate-50 transition-all duration-200 flex items-center justify-center"
                        onClick={() => moveToStructure(index)}
                      >
                        Move To Structure
                      </button>
                    </div>
                  </div>
                </li>
              );
            } else {
              // Render individual model item (not in any group)
              const index = item.originalIndex;
              const modelRef = item.model;

              return (
                <li
                  key={index}
                  className="cursor-pointer mx-3 my-3 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <div className="p-3.5 flex flex-col space-y-3">
                    <div className="flex justify-between items-center w-full">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <button
                            style={{ backgroundColor: colors[index], boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)" }}
                            className="w-5 h-5 rounded-full border border-slate-200 hover:scale-110 transition-transform"
                            onClick={() => toggleColorPicker(`individual-${index}`)}
                          ></button>

                          {showColorPicker[`individual-${index}`] && (
                            <div className="absolute z-10 left-0 top-8 rounded-xl bg-white shadow-2xl border border-slate-200 w-56 flex flex-col items-center p-3">
                              <ChromePicker
                                color={colors[index]}
                                onChange={(color) => handleColorChange(color, index)}
                                disableAlpha={true}
                              />
                              <div className="flex space-x-2 mt-3 w-full">
                                <button
                                  className="flex-1 py-1.5 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm font-medium transition-colors"
                                  onClick={() => handleCloseColorPicker(`individual-${index}`)}
                                >
                                  Cancel
                                </button>
                                {!isModelInGroup(index) && (
                                  <button
                                    className="flex-1 py-1.5 text-white rounded-lg bg-[var(--main-color)] hover:bg-[var(--main-color-bold)] text-sm font-medium shadow-md shadow-[var(--main-color)]/20 transition-all"
                                    onClick={() => handleSaveColor(`individual-${index}`, index)}
                                  >
                                    Save
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className={`text-slate-800 font-semibold tracking-tight ${modelRef.name.length > 20 ? "text-xs" : "text-sm"}`}>
                          {modelRef.name}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedModels.has(index)}
                          onChange={() => toggleModelSelection(index)}
                          className="w-4 h-4 cursor-pointer accent-[var(--main-color)] rounded border-slate-300 focus:ring-[var(--main-color)] transition-all"
                          title="Select for batch visibility"
                        />
                        {visibility[index] ? (
                          <VisibilityOutlinedIcon
                            className="text-[var(--main-color)] cursor-pointer hover:scale-110 transition-transform"
                            onClick={() => handleVisibilityChange(index)}
                            fontSize="small"
                          />
                        ) : (
                          <VisibilityOffOutlinedIcon
                            className="text-slate-300 cursor-pointer hover:scale-110 transition-transform hover:text-slate-400"
                            onClick={() => handleVisibilityChange(index)}
                            fontSize="small"
                          />
                        )}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2.5 flex items-center space-x-3 border border-slate-100">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider w-14">Opacity</span>
                      <Slider
                        min={0}
                        max={1}
                        step={0.1}
                        value={opacity[index]}
                        onChange={(value) => handleOpacityChange(index, value)}
                        className="flex-grow"
                        styles={{
                          track: { backgroundColor: anatomyConstants.mainColor, height: 6 },
                          handle: {
                            borderColor: anatomyConstants.mainColor,
                            backgroundColor: "#fff",
                            width: 14,
                            height: 14,
                            marginTop: -4,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                          },
                          rail: { backgroundColor: '#e2e8f0', height: 6 }
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <button
                        className="py-1.5 px-2 bg-white text-slate-700 border border-slate-200 rounded-lg text-xs font-medium hover:border-[var(--main-color)] hover:text-[var(--main-color)] hover:bg-slate-50 transition-all duration-200 flex items-center justify-center shadow-sm"
                        onClick={() => makeOthersInvisible(index)}
                      >
                        Hide Others
                      </button>
                      <button
                        className="py-1.5 px-2 bg-white text-slate-700 border border-slate-200 rounded-lg text-xs font-medium hover:border-[var(--main-color)] hover:text-[var(--main-color)] hover:bg-slate-50 transition-all duration-200 flex items-center justify-center shadow-sm"
                        onClick={() => moveToStructure(index)}
                      >
                        Move To Structure
                      </button>
                    </div>
                  </div>
                </li>
              );
            }
          })}
        </ul>
      </div>

      {/* Footer buffer zone */}
      <div className="bg-slate-50 border-t border-slate-200 text-center p-3 flex-shrink-0 z-10">
        <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">
          Powered by Wellztech
        </p>
      </div>
    </div>
  );
};

export default ModelsSidebar;
